import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

interface Campaign {
  id: string;
  name: string;
  locationCount: number;
  scoredCount: number;
}

interface DeleteCampaignDialogProps {
  campaign: Campaign;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted?: () => void;
  /**
   * Navigate to /dashboard once the delete succeeds. Correct when the deleted
   * campaign is the one currently open, since its view no longer exists. Pass
   * false when deleting some other campaign from a list, where redirecting
   * would kick the user out of the section they are working in.
   */
  navigateAfterDelete?: boolean;
}

export function DeleteCampaignDialog({
  campaign,
  open,
  onOpenChange,
  onDeleted,
  navigateAfterDelete = true,
}: DeleteCampaignDialogProps) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  async function handleDelete() {
    setSubmitting(true);
    try {
      const res = await fetch(`/api/campaigns/${campaign.id}`, {
        method: "DELETE",
      });

      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error ?? "Failed to delete campaign");
      }

      toast.success(`"${campaign.name}" has been deleted`);
      onDeleted?.();
      onOpenChange(false);
      if (navigateAfterDelete) navigate({ to: "/dashboard" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete campaign");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Campaign</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2">
              <p>
                Are you sure you want to delete <strong>{campaign.name}</strong>? This action cannot
                be undone.
              </p>
              {campaign.scoredCount > 0 ? (
                <p className="font-medium text-destructive">
                  This will permanently delete {campaign.scoredCount} scored location
                  {campaign.scoredCount !== 1 ? "s" : ""} and all associated data.
                </p>
              ) : campaign.locationCount > 0 ? (
                <p>
                  This will permanently delete {campaign.locationCount} location
                  {campaign.locationCount !== 1 ? "s" : ""} and all associated data.
                </p>
              ) : (
                <p>This draft campaign has no locations.</p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={submitting}>
            {submitting ? "Deleting..." : "Delete Campaign"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
